import type { ApiClient } from '../../api/client';
import { workerStatusPresentation } from '../../features/people/public';
import type {
  StaffingWorker as ApiStaffingWorker,
  SupervisoryOrganization as ApiSupervisoryOrganization,
} from '../../api/types';
import type {
  StaffingOperationsPort,
  StaffingOrganization,
  StaffingWorker,
  StaffingPlan,
  StaffingPlanValidation,
} from '../../features/staffing/public';
import { collectOffsetPages } from '../../shared/collectOffsetPages';

const asUUID = (value: string) => value as ReturnType<typeof crypto.randomUUID>;

function mapStaffingWorker(worker: ApiStaffingWorker): StaffingWorker {
  const name = worker.descriptor?.trim() || '이름 미등록';
  const job = worker.primaryJob;
  return {
    id: worker.id,
    personId: worker.person?.id || '',
    workerNumber: worker.workerId || '사번 미등록',
    name,
    initials: Array.from(name)[0] ?? '?',
    profilePhotoUrl: worker.person?.profilePhotoUrl,
    organizationId: job?.supervisoryOrganization?.id,
    organization: job?.supervisoryOrganization?.descriptor || '미배치',
    title: job?.businessTitle || job?.jobProfile?.descriptor || job?.descriptor || '—',
    jobProfile: job?.jobProfile?.descriptor,
    jobProfileId: job?.jobProfile?.id,
    jobLevel: job?.jobLevel?.descriptor,
    jobLevelId: job?.jobLevel?.id,
    businessTitle: job?.businessTitle,
    jobType: job?.jobType?.descriptor,
    jobTypeId: job?.jobType?.id,
    timeType: job?.timeType,
    location: job?.location?.descriptor || '—',
    locationId: job?.location?.id,
    workSpace: job?.workSpace?.descriptor,
    workSpaceId: job?.workSpace?.id,
    status: workerStatusPresentation(worker.status, worker.terminationDate).label,
    workerTypeId: worker.workerType?.id,
    companyId: worker.company?.id,
    company: worker.company?.descriptor,
    managerId: job?.manager?.id,
    manager: job?.manager?.descriptor,
    additionalOrganizations: (worker.additionalJobs ?? []).flatMap(item => item.supervisoryOrganization ? [item.supervisoryOrganization] : []),
  };
}

function mapStaffingOrganization(organization: ApiSupervisoryOrganization): StaffingOrganization {
  return {
    id: organization.id,
    name: organization.descriptor || organization.organizationCode || '이름 미등록',
    subtitle: organization.organizationCode || '감독 조직',
    manager: organization.manager?.descriptor || '미지정',
    managerId: organization.manager?.id ?? null,
    count: organization.memberCount ?? 0,
    location: organization.location?.descriptor || '미지정',
    parentId: organization.parent?.id,
  };
}

export function createStaffingOperationsHttpAdapter(client: ApiClient): StaffingOperationsPort {
  return {
    listPlans() {
      return collectOffsetPages((offset) => client.requestTenant<{ data: StaffingPlan[]; total: number }>(`/api/hr/v1/{tenant}/staffingPlans?limit=100&offset=${offset}`), 100);
    },
    getPlan(id) {
      return client.requestTenant<StaffingPlan>(`/api/hr/v1/{tenant}/staffingPlans/${encodeURIComponent(id)}`);
    },
    createPlan(input, idempotencyKey) {
      return client.requestTenant<StaffingPlan>('/api/hr/v1/{tenant}/staffingPlans', { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input) });
    },
    updatePlan(id, input, version, idempotencyKey) {
      return client.requestTenant<StaffingPlan>(`/api/hr/v1/{tenant}/staffingPlans/${encodeURIComponent(id)}`, { method: 'PATCH', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify({ ...input, version }) });
    },
    validatePlan(id, version) {
      return client.requestTenant<StaffingPlanValidation>(`/api/hr/v1/{tenant}/staffingPlans/${encodeURIComponent(id)}/validate`, { method: 'POST', body: JSON.stringify({ version }) });
    },
    submitPlan(id, version, idempotencyKey) {
      return client.requestTenant<StaffingPlan>(`/api/hr/v1/{tenant}/staffingPlans/${encodeURIComponent(id)}/submit`, { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify({ version }) });
    },
    cancelPlan(id, version, idempotencyKey) {
      return client.requestTenant<StaffingPlan>(`/api/hr/v1/{tenant}/staffingPlans/${encodeURIComponent(id)}/cancel`, { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify({ version }) });
    },
    cancelSubmittedPlan(businessProcessId, comment, idempotencyKey) { return client.cancelBusinessProcessEvent(businessProcessId, comment, asUUID(idempotencyKey)); },
    async getWorker(id, asOf) {
      return mapStaffingWorker(await client.getWorker(id, asOf));
    },
    async listWorkers(search, asOf) {
      return (await (search.trim() ? client.listWorkers(search, asOf) : client.listAllWorkers('', asOf))).data.map(mapStaffingWorker);
    },
    async listOrganizations(asOf) {
      return (await client.listAllSupervisoryOrganizations(asOf)).data.map(mapStaffingOrganization);
    },
    async listCompanies() { return (await client.listHireCompanies()).data; },
    previewWorkerTransition(workerId, input) { return client.previewWorkerTransition(workerId, input); },
    createWorkerTransition(workerId, input, idempotencyKey) { return client.createWorkerTransition(workerId, input, asUUID(idempotencyKey)); },
    async listLocations(asOf) {
      return (await client.listHireLocations(asOf)).data;
    },
    async listJobProfiles(asOf) {
      return (await client.listHireJobProfiles(asOf)).data;
    },
    async listJobLevels(asOf) {
      return (await client.listHireJobLevels(asOf)).data;
    },
    async listJobTypes(workerType, asOf) {
      return (await client.listHireJobTypes(workerType, asOf)).data;
    },
    async listWorkSpaces(locationId, asOf) {
      return (await client.listHireWorkSpaces(locationId, asOf)).data;
    },
    listChanges(processType, status, effectiveFrom) {
      return client.listStaffingChanges(processType, status, effectiveFrom);
    },
    createJobChange(workerId, input, idempotencyKey) {
      return client.createJobChange(workerId, input, asUUID(idempotencyKey));
    },
    getWorkerLifecycle(eventId) { return client.getWorkerLifecycle(eventId); },
    getWorkerLifecycleDraft(eventId) { return client.getWorkerLifecycleDraft(eventId); },
    updateWorkerLifecycleDraft(eventId, input, idempotencyKey) { return client.updateWorkerLifecycleDraft(eventId, input, idempotencyKey); },
    previewWorkerLifecycleDraft(eventId, input) { return client.previewWorkerLifecycleDraft(eventId, input); },
    cancelWorkerLifecycle(businessProcessId, comment, idempotencyKey) { return client.cancelBusinessProcessEvent(businessProcessId, comment, asUUID(idempotencyKey)); },
    createWorkerLifecycle(workerId, type, input, idempotencyKey) {
      return client.createWorkerLifecycle(workerId, type, input, asUUID(idempotencyKey));
    },
    submitJobChange(changeId, idempotencyKey) {
      return client.submitJobChange(changeId, asUUID(idempotencyKey));
    },
    submitWorkerLifecycle(changeId, idempotencyKey, expectedVersion) {
      return client.submitWorkerLifecycle(changeId, asUUID(idempotencyKey), expectedVersion);
    },
    previewWorkerLifecycle(workerId, input) {
      return client.requestTenant(`/api/hr/v1/{tenant}/workers/${encodeURIComponent(workerId)}/workerLifecyclePreview`, { method: 'POST', body: JSON.stringify(input) });
    },
    previewWorkerLifecycleCorrection(changeId, input) {
      return client.requestTenant(`/api/hr/v1/{tenant}/workerLifecycleEvents/${encodeURIComponent(changeId)}/correctionPreview`, { method: 'POST', body: JSON.stringify(input) });
    },
    correctWorkerLifecycle(changeId, input, idempotencyKey) {
      return client.correctWorkerLifecycle(changeId, input, asUUID(idempotencyKey));
    },
  };
}
